/**
 * Citation Parser
 *
 * Parses inline citation markers in formats:
 * - Positional format: @cite:N:"content":"uuid":"filename" (preferred)
 * - Named format: @cite:N:"content":doc="uuid":name="filename" (legacy)
 * - Bracket format: [cite:N:...] (legacy)
 *
 * where N is a sequential number, content is the quoted text, uuid is the document ID, and filename is the document name
 */ // Positional format (preferred): @cite:N:"content":"uuid":"filename"
// All arguments are colon-separated and quoted
// Group 1: Citation ID (number)
// Group 2: Quoted content
// Group 3: Document UUID
// Group 4: Document name (filename)
const SOURCE_ID = '(?:[0-9A-Fa-f]{32}|[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}|att_[0-9]{13}_[a-z0-9]{8})';
const QUOTED = String.raw`((?:\\.|[^"\\])+)`;
const POSITIONAL_PATTERN = new RegExp(String.raw`@cite:(\d+):"${QUOTED}":"(${SOURCE_ID})":"${QUOTED}"(?::range=(\d+)-(\d+):sha256=([a-f0-9]{64}))?`, 'g');
const NAMED_PATTERN = new RegExp(String.raw`(?:@cite:|[[［【]cite:)(\d+):"${QUOTED}":doc[=:]"(${SOURCE_ID})":name="${QUOTED}"[\]】］]?`, 'g');
function decodeQuoted(value) {
    try {
        return JSON.parse(`"${value.replace(/\r?\n/g, '\\n')}"`);
    } catch  {
        return value;
    }
}
/**
 * Creates a fresh regex instance for positional citation matching.
 */ export function createPositionalRegex() {
    return new RegExp(POSITIONAL_PATTERN.source, POSITIONAL_PATTERN.flags);
}
/**
 * Creates a fresh regex instance for named (legacy) citation matching.
 */ export function createNamedRegex() {
    return new RegExp(NAMED_PATTERN.source, NAMED_PATTERN.flags);
}
/**
 * Creates a fresh regex instance for citation matching.
 * @deprecated Use createPositionalRegex() for new code. This returns the named pattern for backward compatibility.
 */ export function createCitationRegex() {
    return createNamedRegex();
}
/**
 * @deprecated Use createCitationRegex() instead to avoid global regex state issues.
 * This export is maintained for backward compatibility but creates a fresh instance each access.
 */ export const CITATION_REGEX = createCitationRegex();
/**
 * Parse citations from text and build a citation map
 * Supports both positional format (@cite:N:"content":"uuid":"filename")
 * and named format (@cite:N:"content":doc="uuid":name="filename")
 *
 * @param text - Text containing citation markers
 * @returns Object with matches array and citationMap
 */ export function parseCitations(text) {
    const matches = [];
    const citationMap = new Map();
    try {
        // Try positional format first (preferred)
        const positionalRegex = createPositionalRegex();
        for (const match of text.matchAll(positionalRegex)){
            const id = Number.parseInt(match[1], 10);
            const content = decodeQuoted(match[2]);
            const documentId = match[3];
            const documentName = decodeQuoted(match[4]);
            matches.push({
                fullMatch: match[0],
                id,
                content,
                documentId,
                documentName,
                startIndex: match.index,
                endIndex: match.index + match[0].length
            });
            citationMap.set(id, {
                id,
                content,
                documentId,
                documentName,
                ...match[5] !== undefined && Number.isSafeInteger(Number(match[5])) && Number.isSafeInteger(Number(match[6])) && Number(match[6]) > Number(match[5]) ? {
                    sourceAnchor: {
                        start: Number(match[5]),
                        end: Number(match[6]),
                        hash: match[7]
                    }
                } : {}
            });
        }
        // Also try named format (legacy) for any citations not yet matched
        const namedRegex = createNamedRegex();
        for (const match of text.matchAll(namedRegex)){
            const id = Number.parseInt(match[1], 10);
            // Skip if already matched by positional pattern
            if (citationMap.has(id)) continue;
            const content = decodeQuoted(match[2]);
            const documentId = match[3];
            const documentName = decodeQuoted(match[4]);
            matches.push({
                fullMatch: match[0],
                id,
                content,
                documentId,
                documentName,
                startIndex: match.index,
                endIndex: match.index + match[0].length
            });
            citationMap.set(id, {
                id,
                content,
                documentId,
                documentName
            });
        }
        // Sort matches by startIndex to maintain document order
        matches.sort((a, b)=>a.startIndex - b.startIndex);
    } catch  {
        // Silent fail - return empty results
        return {
            matches: [],
            citationMap: new Map()
        };
    }
    return {
        matches,
        citationMap
    };
}
/**
 * Remove all citation markers from text
 * Supports both positional and named formats
 *
 * @param text - Text containing citation markers
 * @returns Clean text without citation markers
 */ export function removeCitationMarkers(text) {
    try {
        // Remove both formats
        return text.replace(createPositionalRegex(), '').replace(createNamedRegex(), '');
    } catch  {
        return text;
    }
}
/**
 * Check if text contains any citations
 * Supports both positional and named formats
 *
 * @param text - Text to check
 * @returns True if text contains citation markers
 */ export function hasCitations(text) {
    try {
        return createPositionalRegex().test(text) || createNamedRegex().test(text);
    } catch  {
        return false;
    }
}
/**
 * Replace citation markers with custom content using a replacer function
 * Supports both positional and named formats
 *
 * @param text - Text containing citation markers
 * @param replacer - Function that receives (fullMatch, citationId) and returns replacement string
 * @returns Text with citation markers replaced
 */ export function replaceCitationMarkers(text, replacer) {
    try {
        // Replace positional format first
        let result = text.replace(createPositionalRegex(), (match, id)=>replacer(match, id));
        // Then replace named format
        result = result.replace(createNamedRegex(), (match, id)=>replacer(match, id));
        return result;
    } catch  {
        return text;
    }
}
